import { useState, useEffect, useCallback } from "react";
import { Card, Label, Button } from "../components/Primitives";
import storage from "../utils/storage";

// ─── MOBILITY PROTOCOLS ─────────────────────────────────────
// Interactive stretching, foam rolling, and recovery routines
// Data feeds to client AI via completion tracking
// ─────────────────────────────────────────────────────────────

const PROTOCOLS = [
  { id: "stretch", title: "Stretching", subtitle: "Static holds for flexibility and injury prevention", items: [
    { id: "hip_flexor", name: "Hip Flexor Stretch", duration: 60, sides: 2, when: "Post-workout + before bed", cue: "Kneel, drive hips forward, squeeze glute of rear leg. Tight hip flexors inhibit glute activation and cause anterior pelvic tilt." },
    { id: "hamstring", name: "Hamstring Stretch", duration: 45, sides: 2, when: "Post-workout", cue: "Straight leg on bench, hinge forward at hips. Never round the lower back — the stretch should come from the hamstring, not spinal flexion." },
    { id: "chest_shoulder", name: "Chest/Shoulder Doorway Stretch", duration: 45, sides: 2, when: "Post push days", cue: "Arm on doorframe at 90 degrees, lean through gently. Prevents internal rotation and rounded shoulders from heavy pressing." },
    { id: "lat", name: "Lat Stretch", duration: 30, sides: 2, when: "Post pull days", cue: "Grab overhead bar or doorframe, lean away. Opens thoracic spine and improves overhead mobility." },
    { id: "quad", name: "Quad Stretch", duration: 45, sides: 2, when: "Post leg days", cue: "Standing or lying — pull heel to glute. Add hip extension for a deeper rectus femoris stretch." },
    { id: "piriformis", name: "Piriformis/Glute Stretch", duration: 45, sides: 2, when: "Post lower body", cue: "Figure-4 stretch: ankle over knee, pull thigh toward chest. Relieves sciatic tension and hip tightness." },
    { id: "calf", name: "Calf Stretch", duration: 30, sides: 2, when: "Post leg days", cue: "Step one foot back, press heel down, lean forward into wall. Straight leg hits gastrocnemius, bent knee hits soleus." },
    { id: "traps_neck", name: "Trap / Neck Release", duration: 30, sides: 2, when: "Post upper body", cue: "Tilt head to one side, gently pull with opposite hand. Keep shoulder down. Never force — neck muscles are small and respond to gentle sustained pressure." },
  ]},
  { id: "foam", title: "Foam Rolling", subtitle: "Myofascial release for recovery and mobility", items: [
    { id: "it_band", name: "IT Band / TFL", duration: 60, sides: 2, when: "Pre-workout or rest days", cue: "Roll from hip to above knee — pause on tender spots 15-20 seconds. Never roll directly on the knee joint." },
    { id: "t_spine", name: "Thoracic Spine", duration: 60, sides: 1, when: "Daily", cue: "Upper back on roller, arms crossed, extend over roller. Improves thoracic extension for squats, overhead press, and posture." },
    { id: "glutes_foam", name: "Glutes / Piriformis", duration: 60, sides: 2, when: "Post lower body", cue: "Sit on roller, cross ankle over knee, roll through glute. Use a lacrosse ball for deeper trigger point work." },
    { id: "quads_foam", name: "Quads", duration: 45, sides: 2, when: "Post leg days", cue: "Face down, roller under one quad, roll slowly. Pause on the VMO (inner quad above knee) for extra attention." },
    { id: "calves_foam", name: "Calves", duration: 30, sides: 2, when: "Post leg days", cue: "Stack legs on roller, roll from ankle to below knee. Cross one leg over the other for increased pressure." },
    { id: "lats_foam", name: "Lats / Teres Major", duration: 45, sides: 2, when: "Post pull days", cue: "Lie on side with roller under armpit area. Roll from armpit to mid-rib. Opens up lat tightness from heavy pulling." },
  ]},
  { id: "active", title: "Active Recovery", subtitle: "Low-intensity movement for rest days", items: [
    { id: "walk", name: "Light Walking", duration: 1200, sides: 1, when: "Rest days", cue: "Zone 1 — easy pace, promotes blood flow and nutrient delivery to recovering muscles without creating additional fatigue." },
    { id: "breathing", name: "Diaphragmatic Breathing", duration: 300, sides: 1, when: "Pre-sleep", cue: "4-count inhale, 7-count hold, 8-count exhale. Activates parasympathetic nervous system, reduces cortisol, improves sleep onset." },
    { id: "contrast", name: "Contrast Showers", duration: 420, sides: 1, when: "Post-workout", cue: "Alternate 30s hot / 30s cold for 4-6 cycles, always end cold. Enhances circulation, reduces inflammation, improves recovery." },
  ]},
];

function formatTime(s) {
  if (s >= 60) { const m = Math.floor(s / 60); const r = s % 60; return r ? `${m}:${String(r).padStart(2, "0")}` : `${m}:00`; }
  return `0:${String(s).padStart(2, "0")}`;
}

function Timer({ duration, onComplete, C }) {
  const [remaining, setRemaining] = useState(duration);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!running || remaining <= 0) return;
    const t = setInterval(() => setRemaining(r => {
      if (r <= 1) { clearInterval(t); onComplete?.(); return 0; }
      return r - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [running, remaining]);

  const pct = ((duration - remaining) / duration) * 100;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "var(--sp-md)" }}>
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: C.glass010, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: C.accent, borderRadius: 3, transition: "width 1s linear" }} />
      </div>
      <span style={{ fontFamily: "var(--m)", fontSize: "var(--fs-lg)", color: remaining === 0 ? C.accent : C.text1, minWidth: 50, textAlign: "right" }}>
        {formatTime(remaining)}
      </span>
      {remaining > 0 ? (
        <button onClick={() => setRunning(!running)} style={{
          background: running ? C.glass010 : C.accent, color: running ? C.text1 : "#000",
          border: "none", borderRadius: 8, padding: "6px 14px", fontSize: "var(--fs-sm)",
          fontFamily: "var(--m)", fontWeight: 600, cursor: "pointer",
        }}>
          {running ? "Pause" : "Start"}
        </button>
      ) : (
        <span style={{ color: C.accent, fontFamily: "var(--m)", fontSize: "var(--fs-sm)", fontWeight: 600 }}>Done</span>
      )}
    </div>
  );
}

export default function MobilityView({ C, onBack }) {
  const todayKey = new Date().toISOString().split("T")[0];
  const [activeProtocol, setActiveProtocol] = useState(null);
  const [completed, setCompleted] = useState(() => storage.get(`mobility_${todayKey}`, {}));
  const [expandedItem, setExpandedItem] = useState(null);

  const saveCompleted = useCallback((next) => {
    setCompleted(next);
    storage.set(`mobility_${todayKey}`, next);
  }, [todayKey]);

  const markDone = useCallback((itemId) => {
    const next = { ...completed, [itemId]: true };
    saveCompleted(next);
  }, [completed, saveCompleted]);

  const protocol = activeProtocol ? PROTOCOLS.find(p => p.id === activeProtocol) : null;

  // Count completed per protocol
  const protocolProgress = PROTOCOLS.map(p => {
    const total = p.items.length;
    const done = p.items.filter(i => completed[i.id]).length;
    return { ...p, done, total };
  });

  const totalDone = Object.keys(completed).length;
  const totalItems = PROTOCOLS.reduce((a, p) => a + p.items.length, 0);

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--sp-xl)" }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: C.accent, fontSize: "var(--fs-base)", fontFamily: "var(--m)", cursor: "pointer", padding: 0 }}>
          Back
        </button>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "var(--fs-2xs)", color: C.text4, fontFamily: "var(--m)", letterSpacing: ".1em" }}>TODAY</div>
          <div style={{ fontSize: "var(--fs-sm)", color: totalDone > 0 ? C.accent : C.text3, fontFamily: "var(--m)" }}>
            {totalDone}/{totalItems} completed
          </div>
        </div>
      </div>

      <Label C={C} style={{ fontSize: "var(--fs-h2)", marginBottom: "var(--sp-sm)" }}>Mobility</Label>
      <div style={{ fontSize: "var(--fs-sm)", color: C.text3, marginBottom: "var(--sp-xl)", fontFamily: "var(--b)" }}>
        Stretching, foam rolling, and recovery protocols
      </div>

      {!protocol ? (
        /* ─── PROTOCOL SELECTOR ─── */
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-md)" }}>
          {protocolProgress.map(p => (
            <Card key={p.id} C={C} onClick={() => setActiveProtocol(p.id)} style={{ padding: "var(--card-pad)", cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <Label C={C} style={{ marginBottom: 2 }}>{p.title}</Label>
                  <div style={{ fontSize: "var(--fs-sm)", color: C.text3 }}>{p.subtitle}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "var(--fs-lg)", fontFamily: "var(--m)", color: p.done === p.total && p.total > 0 ? C.accent : C.text1 }}>
                    {p.done}/{p.total}
                  </div>
                  <div style={{ fontSize: "var(--fs-2xs)", color: C.text4, fontFamily: "var(--m)" }}>
                    {p.items.length} exercises
                  </div>
                </div>
              </div>
              {/* Progress bar */}
              {p.done > 0 && (
                <div style={{ marginTop: "var(--sp-sm)", height: 3, borderRadius: 2, background: C.glass010, overflow: "hidden" }}>
                  <div style={{ width: `${(p.done / p.total) * 100}%`, height: "100%", background: C.accent, borderRadius: 2 }} />
                </div>
              )}
            </Card>
          ))}
        </div>
      ) : (
        /* ─── ACTIVE PROTOCOL ─── */
        <div>
          <button onClick={() => { setActiveProtocol(null); setExpandedItem(null); }} style={{
            background: "none", border: "none", color: C.accent, fontSize: "var(--fs-sm)",
            fontFamily: "var(--m)", cursor: "pointer", padding: 0, marginBottom: "var(--sp-lg)",
          }}>
            All Protocols
          </button>
          <Label C={C} style={{ fontSize: "var(--fs-h3)", marginBottom: "var(--sp-lg)" }}>{protocol.title}</Label>

          <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-sm)" }}>
            {protocol.items.map(item => {
              const isDone = completed[item.id];
              const isExpanded = expandedItem === item.id;
              return (
                <Card key={item.id} C={C} style={{ padding: "var(--card-pad)", opacity: isDone ? 0.6 : 1 }}>
                  <div onClick={() => setExpandedItem(isExpanded ? null : item.id)} style={{ cursor: "pointer" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "var(--sp-sm)" }}>
                        {isDone && <span style={{ color: C.accent, fontSize: "var(--fs-base)" }}>&#10003;</span>}
                        <span style={{ fontSize: "var(--fs-base)", color: C.text1, fontWeight: 600, textDecoration: isDone ? "line-through" : "none" }}>
                          {item.name}
                        </span>
                      </div>
                      <span style={{ fontSize: "var(--fs-sm)", color: C.text4, fontFamily: "var(--m)" }}>
                        {item.duration >= 60 ? `${Math.floor(item.duration / 60)}m` : `${item.duration}s`}{item.sides === 2 ? " /side" : ""}
                      </span>
                    </div>
                    <div style={{ fontSize: "var(--fs-2xs)", color: C.text4, fontFamily: "var(--m)", marginTop: 2 }}>
                      {item.when}
                    </div>
                  </div>

                  {isExpanded && !isDone && (
                    <div style={{ marginTop: "var(--sp-md)", paddingTop: "var(--sp-md)", borderTop: `1px solid ${C.glass010}` }}>
                      <div style={{ fontSize: "var(--fs-sm)", color: C.text2, marginBottom: "var(--sp-md)", lineHeight: 1.5 }}>
                        {item.cue}
                      </div>
                      {item.sides === 2 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-sm)" }}>
                          <div>
                            <div style={{ fontSize: "var(--fs-2xs)", color: C.text4, fontFamily: "var(--m)", marginBottom: 4 }}>LEFT SIDE</div>
                            <Timer duration={item.duration} C={C} onComplete={() => {}} />
                          </div>
                          <div>
                            <div style={{ fontSize: "var(--fs-2xs)", color: C.text4, fontFamily: "var(--m)", marginBottom: 4 }}>RIGHT SIDE</div>
                            <Timer duration={item.duration} C={C} onComplete={() => markDone(item.id)} />
                          </div>
                        </div>
                      ) : (
                        <Timer duration={item.duration} C={C} onComplete={() => markDone(item.id)} />
                      )}
                      <button onClick={() => markDone(item.id)} style={{
                        marginTop: "var(--sp-md)", width: "100%", padding: "8px", background: C.glass010,
                        border: "none", borderRadius: 8, color: C.text3, fontSize: "var(--fs-sm)",
                        fontFamily: "var(--m)", cursor: "pointer",
                      }}>
                        Skip / Mark Done
                      </button>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
